// Shared non-component exports; keep component modules valid Fast Refresh boundaries.

import {
  type AgentRow,
  type CustomDateRange,
  type MyTeam,
  type RangeMode,
  type RoundtableDirection,
  type RoundtableQaIn,
  type TimeRange,
} from "../lib/api";
import { rangeTitle } from "../components/TimeRangeControls.helpers";

// Max panel size mirrors the backend cap (_MAX_AGENTS) so the UI never
// lets the user assemble a panel the server would silently truncate.
// PET-658 raised this from 10 to 12 in lockstep with the BE cap; the
// "n/12" counter, the "Panel is full" states, and the team-add
// truncation all derive from this single constant. FE/BE parity is
// pinned in the test suites so the two can't drift.
export const MAX_PANEL = 12;

export const MAX_CALLS_PER_AGENT = 100;

export const DIRECTION_OPTIONS: readonly {
  id: RoundtableDirection;
  label: string;
}[] = [
  { id: "both", label: "Both" },
  { id: "inbound", label: "Inbound" },
  { id: "outbound", label: "Outbound" },
];

// ---------------------------------------------------------------------------
// Pure helpers (exported for unit tests)
// ---------------------------------------------------------------------------

export function rowLabel(r: AgentRow): string {
  return r.full_name || r.email || r.agent_id || "—";
}

// Add one agent row to the panel: deduped by agent_id, capped at
// MAX_PANEL, input order preserved. Returns the panel unchanged (same
// reference) when the add is a no-op.
export function addAgentToPanel(
  panel: AgentRow[],
  row: AgentRow,
): AgentRow[] {
  if (!row.agent_id) return panel;
  if (panel.length >= MAX_PANEL) return panel;
  if (panel.some((a) => a.agent_id === row.agent_id)) return panel;
  return [...panel, row];
}

// Reconcile a team's member list against the caller's visibility-scoped
// agent rows and bulk-add — exactly as if each member had been added
// individually. Members absent from the scoped list are SILENTLY
// skipped (owner decision: no notice for members the caller can't see);
// the dismissable cap-overflow notice counts only visible members that
// didn't fit.
export function reconcileTeamAdd(
  members: readonly { agent_id: string }[],
  allAgents: readonly AgentRow[],
  panel: AgentRow[],
  teamName: string,
): { nextPanel: AgentRow[]; notice: string | null } {
  const byId = new Map(
    allAgents
      .filter((a) => a.agent_id)
      .map((a) => [a.agent_id as string, a]),
  );
  const chosen = new Set(panel.map((a) => a.agent_id));
  const candidates: AgentRow[] = [];
  for (const m of members) {
    const row = byId.get(m.agent_id);
    // Out-of-scope member (not in the caller's agent list) — skip
    // silently; already-chosen member — dedupe.
    if (!row || !row.agent_id || chosen.has(row.agent_id)) continue;
    chosen.add(row.agent_id);
    candidates.push(row);
  }
  const remaining = Math.max(0, MAX_PANEL - panel.length);
  const added = candidates.slice(0, remaining);
  const skipped = candidates.length - added.length;
  return {
    nextPanel: added.length > 0 ? [...panel, ...added] : panel,
    notice:
      skipped > 0
        ? `Panel is full — ${skipped} member${skipped === 1 ? "" : "s"} of “${teamName}” ${
            skipped === 1 ? "was" : "were"
          } not added.`
        : null,
  };
}

// Agents matching the typed text, excluding panelists, capped for the
// dropdown. The list itself is already visibility-scoped by the server.
export function filterSelectableAgents(
  allAgents: readonly AgentRow[],
  panel: readonly AgentRow[],
  query: string,
): AgentRow[] {
  const chosen = new Set(panel.map((a) => a.agent_id));
  const q = query.trim().toLowerCase();
  return allAgents
    .filter((r) => r.agent_id && !chosen.has(r.agent_id))
    .filter((r) => (q ? rowLabel(r).toLowerCase().includes(q) : true))
    .slice(0, 20);
}

// Teams whose name matches the typed text. Shown as their own group at
// the top of the dropdown; selecting one bulk-adds every visible member.
export function filterMatchableTeams(
  teams: readonly MyTeam[],
  query: string,
): MyTeam[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return teams.filter((t) => t.name.toLowerCase().includes(q)).slice(0, 10);
}

// Enter commits the top suggestion — a matched team takes precedence
// over an agent (teams are listed first in the dropdown).
export function topSuggestion(
  teams: readonly MyTeam[],
  agents: readonly AgentRow[],
):
  | { kind: "team"; team: MyTeam }
  | { kind: "agent"; row: AgentRow }
  | null {
  const team = teams[0];
  if (team) return { kind: "team", team };
  const row = agents[0];
  if (row) return { kind: "agent", row };
  return null;
}

export function effectiveMode(timeRange: TimeRange, rangeMode: RangeMode): RangeMode {
  return timeRange === "daily" ||
    timeRange === "all_time" ||
    timeRange === "custom"
    ? "calendar"
    : rangeMode;
}

// Whether the current selection bounds the pool. All-time (and a
// half-specified custom pair, which the shared control never commits
// but a defensive check costs nothing) means "no date filter".
export function isUnbounded(
  timeRange: TimeRange,
  customRange: CustomDateRange | null,
): boolean {
  if (timeRange === "all_time") return true;
  if (timeRange === "custom" && !(customRange?.start && customRange?.end))
    return true;
  return false;
}

// Request payload for one Roundtable question. Mirrors the BE
// RoundtableQaIn contract: custom sends both bounds, every other preset
// sends the range vocabulary and lets the server resolve the window; an
// unbounded selection degrades to all_time (no bounds).
export function buildRoundtableQaIn(args: {
  panel: readonly AgentRow[];
  question: string;
  timeRange: TimeRange;
  rangeMode: RangeMode;
  customRange: CustomDateRange | null;
  direction: RoundtableDirection;
  hasTranscript: boolean;
}): RoundtableQaIn {
  const unbounded = isUnbounded(args.timeRange, args.customRange);
  const timeRange: TimeRange = unbounded ? "all_time" : args.timeRange;
  const isCustom = timeRange === "custom" && args.customRange != null;
  return {
    agent_ids: args.panel
      .map((a) => a.agent_id)
      .filter((id): id is string => !!id),
    question: args.question,
    time_range: timeRange,
    range_mode: effectiveMode(timeRange, args.rangeMode),
    start: isCustom && args.customRange ? args.customRange.start : null,
    end: isCustom && args.customRange ? args.customRange.end : null,
    direction: args.direction,
    has_transcript: args.hasTranscript,
  };
}

// Caption under the scope controls: says what the next question will
// analyse. Bounded ranges bound the pool (server-enforced) — the
// caption reflects that honestly.
export function scopeCaption(
  timeRange: TimeRange,
  rangeMode: RangeMode,
  customRange: CustomDateRange | null,
): string {
  if (isUnbounded(timeRange, customRange)) {
    return `No date filter — analyzing each agent's ${MAX_CALLS_PER_AGENT} most recent matching calls.`;
  }
  const title = rangeTitle(
    timeRange,
    effectiveMode(timeRange, rangeMode),
    customRange,
  );
  return `Analyzing each agent's ${MAX_CALLS_PER_AGENT} most recent matching calls within ${title} — a bounded range never pulls calls from outside it.`;
}

// Short scope label snapshotted into each block at ask time.
export function buildScopeLabel(args: {
  timeRange: TimeRange;
  rangeMode: RangeMode;
  customRange: CustomDateRange | null;
  direction: RoundtableDirection;
  hasTranscript: boolean;
}): string {
  const range = isUnbounded(args.timeRange, args.customRange)
    ? `Last ${MAX_CALLS_PER_AGENT} calls per agent`
    : rangeTitle(
        args.timeRange,
        effectiveMode(args.timeRange, args.rangeMode),
        args.customRange,
      );
  const dir =
    DIRECTION_OPTIONS.find((d) => d.id === args.direction)?.label ?? "Both";
  return `${range} · ${dir}${args.hasTranscript ? " · transcript only" : ""}`;
}
